import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Card,
  Alert,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelopeCircleCheck,
  faKey,
  faUserPen,
  faCircleCheck,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../firebaseContext";

export default function Profile() {
  const {
    currentUser,
    resetPassword,
    getUserData,
    updateUser,
    sendVerifyEmail,
  } = useAuth();

  const [profileData, setProfileData] = useState(null);
  const [displayNameValue, setDisplayNameValue] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [profileAlert, setProfileAlert] = useState(null);
  const [verifyAlert, setVerifyAlert] = useState(null);
  const [resetAlert, setResetAlert] = useState(null);

  useEffect(() => {
    if (!currentUser?.email) return;
    let active = true;
    async function loadProfile() {
      try {
        const snap = await getUserData(currentUser.email);
        if (!active) return;
        const payload = snap?.data?.();
        if (payload) {
          setProfileData(payload);
          setDisplayNameValue(payload.Display || "");
        }
      } catch (err) {
        setProfileAlert({ type: "danger", message: "Unable to load profile." });
      } finally {
        if (active) setLoadingProfile(false);
      }
    }
    loadProfile();
    return () => {
      active = false;
    };
  }, [currentUser, getUserData]);

  const verified = Boolean(profileData?.Verified || currentUser?.emailVerified);
  const initials = useMemo(() => {
    const source =
      displayNameValue || profileData?.Display || currentUser?.email || "";
    return (
      source
        .trim()
        .split(/\s+/)
        .map((part) => part[0])
        .filter(Boolean)
        .join("")
        .slice(0, 2)
        .toUpperCase() || "U"
    );
  }, [displayNameValue, profileData, currentUser]);

  async function handleSaveProfile(event) {
    event.preventDefault();
    const nextName = displayNameValue.trim();
    if (!nextName) {
      setProfileAlert({ type: "danger", message: "Display name is required." });
      return;
    }
    setBusyAction("save");
    setProfileAlert(null);
    try {
      await updateUser(nextName);
      setProfileData((prev) => (prev ? { ...prev, Display: nextName } : prev));
      setDisplayNameValue(nextName);
      setProfileAlert({ type: "success", message: "Profile updated." });
    } catch (err) {
      setProfileAlert({ type: "danger", message: "Update failed." });
    } finally {
      setBusyAction("");
    }
  }

  async function handleSendVerification() {
    setBusyAction("verify");
    setVerifyAlert(null);
    try {
      await sendVerifyEmail(currentUser);
      setVerifyAlert({ type: "success", message: "Verification email sent." });
    } catch (err) {
      setVerifyAlert({
        type: "danger",
        message: "Unable to send verification.",
      });
    } finally {
      setBusyAction("");
    }
  }

  async function handlePasswordReset() {
    setBusyAction("reset");
    setResetAlert(null);
    try {
      await resetPassword(currentUser.email);
      setResetAlert({ type: "success", message: "Password reset email sent." });
    } catch (err) {
      setResetAlert({
        type: "danger",
        message: "Unable to send password reset.",
      });
    } finally {
      setBusyAction("");
    }
  }

  if (!currentUser) return null;

  if (loadingProfile) {
    return (
      <Container
        fluid="lg"
        className="profile-page py-5 d-flex justify-content-center"
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <>
      <Container fluid="lg" className="profile-page py-5">
        <Row className="g-4">
          <Col lg={4}>
            <Card className="profile-card h-100 text-center">
              <Card.Body>
                <div className="profile-avatar">{initials}</div>
                <h5 className="mt-3 mb-1">
                  {displayNameValue || profileData?.Display || "User"}
                </h5>
                <Badge bg={verified ? "success" : "warning"} className="px-3">
                  {verified ? "Verified" : "Pending"}
                </Badge>
                <div className="profile-meta mt-3">
                  <span>{currentUser.email}</span>
                  {profileData?.UID && <span>UID: {profileData.UID}</span>}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={8}>
            <Card className="profile-card mb-4">
              <Card.Header className="profile-card-header">Account</Card.Header>
              <Card.Body>
                {profileAlert && (
                  <Alert
                    variant={profileAlert.type}
                    onClose={() => setProfileAlert(null)}
                    dismissible
                  >
                    {profileAlert.message}
                  </Alert>
                )}
                <Form onSubmit={handleSaveProfile} className="profile-form">
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={currentUser.email}
                      readOnly
                    />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label>Display name</Form.Label>
                    <Form.Control
                      type="text"
                      value={displayNameValue}
                      onChange={(event) =>
                        setDisplayNameValue(event.target.value)
                      }
                      disabled={busyAction === "save"}
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end">
                    <Button
                      type="submit"
                      variant="primary"
                      className="profile-action-btn"
                      disabled={busyAction === "save"}
                    >
                      {busyAction === "save" ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                      ) : (
                        <FontAwesomeIcon icon={faUserPen} className="me-2" />
                      )}
                      Save changes
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
            <Row className="g-4">
              <Col md={6}>
                <Card className="profile-card h-100">
                  <Card.Header className="profile-card-header">
                    Email status
                  </Card.Header>
                  <Card.Body className="d-flex flex-column gap-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Status</span>
                      <Badge bg={verified ? "success" : "warning"}>
                        {verified ? "Verified" : "Pending"}
                      </Badge>
                    </div>
                    {verifyAlert && (
                      <Alert
                        variant={verifyAlert.type}
                        onClose={() => setVerifyAlert(null)}
                        dismissible
                        className="mb-0"
                      >
                        {verifyAlert.message}
                      </Alert>
                    )}
                    <Button
                      variant="primary"
                      onClick={handleSendVerification}
                      disabled={busyAction === "verify" || verified}
                      className="profile-action-btn"
                    >
                      {busyAction === "verify" ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={
                            verified ? faCircleCheck : faEnvelopeCircleCheck
                          }
                          className="me-2"
                        />
                      )}
                      {verified ? "Verified" : "Send verification"}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="profile-card h-100">
                  <Card.Header className="profile-card-header">
                    Password reset
                  </Card.Header>
                  <Card.Body className="d-flex flex-column gap-3">
                    {resetAlert && (
                      <Alert
                        variant={resetAlert.type}
                        onClose={() => setResetAlert(null)}
                        dismissible
                        className="mb-0"
                      >
                        {resetAlert.message}
                      </Alert>
                    )}
                    <Button
                      variant="outline-primary"
                      onClick={handlePasswordReset}
                      disabled={busyAction === "reset"}
                      className="profile-action-btn"
                    >
                      {busyAction === "reset" ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                      ) : (
                        <FontAwesomeIcon icon={faKey} className="me-2" />
                      )}
                      Send reset email
                    </Button>
                    <div className="d-flex align-items-center gap-2 small text-muted">
                      <FontAwesomeIcon icon={faClock} />
                      <span>Link expires after 24 hours.</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
}
